import { useEffect, useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import WebinarDialog from "@/components/WebinarDialog";

// --- Types ---
type SessionType = 'asia' | 'us';

interface Session {
  label: string;
  date: string;
  iso: string;
  calendar: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface Tagline {
  icon: React.ReactNode;
  text: string;
}

// --- Constants ---
const SESSIONS: Record<SessionType, Session> = {
  asia: {
    label: 'Session',
    date: 'June 25th, 11:00 AM IST',
    iso: '2025-06-25T11:00:00+05:30', // fixed ISO for Asia session
    calendar: '#',
  },
  us: {
    label: 'US Session',
    date: 'June 25th, 11:00 AM EST',
    iso: '2025-06-25T11:00:00-05:00',
    calendar: '#',
  },
};

const TAGLINES: Tagline[] = [
  {
    icon: (
      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
        <title>Checkmark Icon</title>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
    text: 'Delivers auto-generated impact reports.'
  },
  {
    icon: (
      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
        <title>Lightning Bolt Icon</title>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    text: 'Books events instantly.'
  },
];

// --- Components ---
const TimerUnit = memo(({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-center mx-1">
    <div className="bg-gray-900 rounded-lg px-4 py-2 text-3xl font-mono font-bold text-white min-w-[52px] text-center select-none">
      {value.toString().padStart(2, '0')}
    </div>
    <div className="text-xs text-gray-400 mt-1 tracking-wider uppercase">{label}</div>
  </div>
));

const AnimatedTimerUnit = memo(({ value, label, flip }: { value: number; label: string; flip: boolean }) => (
  <motion.div
    className="flex flex-col items-center mx-1"
    animate={flip ? { rotateX: [0, 90, 0] } : { rotateX: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="bg-gray-900 rounded-lg px-4 py-2 text-3xl font-mono font-bold text-white min-w-[52px] text-center select-none">
      {value.toString().padStart(2, '0')}
    </div>
    <div className="text-xs text-gray-400 mt-1 tracking-wider uppercase">{label}</div>
  </motion.div>
));

const ProgressBar = memo(({ percent }: { percent: number }) => (
  <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden mb-2">
    <motion.div
      className="h-full bg-gradient-to-r from-[#2D7D46] to-[#3FA65B]"
      initial={{ width: 0 }}
      animate={{ width: `${percent}%` }}
      transition={{ duration: 0.7 }}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      role="progressbar"
    />
  </div>
));

const ConfettiBurst = memo(({ trigger }: { trigger: boolean }) => {
  if (!trigger) return null;
  return (
    <svg className="absolute left-1/2 top-3 z-30" style={{ transform: 'translateX(-50%)' }} width="120" height="40">
      {[...Array(12)].map((_, i) => (
        <circle
          key={i}
          cx={60 + 40 * Math.cos((i / 12) * 2 * Math.PI)}
          cy={20 + 16 * Math.sin((i / 12) * 2 * Math.PI)}
          r={2 + Math.random() * 2}
          fill={["#2D7D46", "#3FA65B", "#fff", "#A7FFEB"][i % 4]}
          style={{
            opacity: 0.8,
            animation: `burst 0.7s cubic-bezier(.7,0,.3,1) forwards`,
            animationDelay: `${i * 0.03}s`
          }}
        />
      ))}
      <style>{"@keyframes burst{0%{transform:scale(0);}100%{transform:scale(1.7) translateY(-28px);opacity:0;}}"}</style>
    </svg>
  );
});

const AuroraGlowBG = memo(() => (
  <div className="fixed inset-0 pointer-events-none z-0">
    <div className="absolute -top-10 left-1/4 w-2/3 h-32 bg-gradient-to-r from-[#2D7D46]/30 via-[#3FA65B]/10 to-[#2D7D46]/30 blur-2xl rounded-full animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-1/2 h-20 bg-gradient-to-l from-[#3FA65B]/20 to-[#2D7D46]/10 blur-2xl rounded-full animate-pulse" />
  </div>
));

// --- Main Component ---
const panelists = [
  {
    name: "Vijay Krishna",
    title: "Co-Founder & CEO, CauseConnect",
    image: "/images/corporate/Cofounder-vijay.png",
    live: true,
  },
  {
    name: "Shoaib Mohammed",
    title: "Co-Founder & CTO, CauseConnect",
    image: "/images/corporate/Cofounder-shoaib.png",
    live: true,
  },
  {
    name: "CORAA AI",
    title: "Copilot for Social Impact",
    image: "/images/coraa-mascot.png",
    live: true,
    isCoraa: true,
  },
];

const WebinarDetailsSection = () => {
  const [active, setActive] = useState<SessionType>('asia');
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLive, setIsLive] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [prevTime, setPrevTime] = useState<TimeLeft | null>(null);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [isWebinarDialogOpen, setIsWebinarDialogOpen] = useState(false);

  const session = SESSIONS[active];

  // Calculate time left
  const calculateTimeLeft = useCallback((): TimeLeft => {
    const now = new Date();
    const target = new Date(session.iso);
    const diff = target.getTime() - now.getTime();
    if (diff <= 0) {
      setIsLive(true);
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / 1000 / 60) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  }, [session.iso]);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = calculateTimeLeft();
      setTimeLeft(newTime);
      if (prevTime && JSON.stringify(newTime) !== JSON.stringify(prevTime)) {
        setConfetti(true);
        setTimeout(() => setConfetti(false), 800);
      }
      setPrevTime(newTime);
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft, prevTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTaglineIndex((idx) => (idx + 1) % TAGLINES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 bg-[#101825] border-t border-gray-800">
      <AuroraGlowBG />
      <div className="max-w-5xl mx-auto px-6 py-10 rounded-2xl shadow-2xl bg-[#131c2b]/90 border border-[#2D7D46]/40 backdrop-blur-md relative overflow-hidden flex flex-col md:flex-row items-stretch gap-10">
        {/* LEFT: Timer Card */}
        <div className="w-full md:w-1/2 flex flex-col items-center md:items-end justify-center">
          <div className="mb-0 w-full flex flex-col items-center md:items-end justify-center h-full">

            {/* Unified Session Switcher & Timer Card */}
            <div className="w-full rounded-2xl bg-[#181f2b]/90 border border-[#3FA65B]/40 shadow-xl px-8 py-8 flex flex-col items-center glassmorphism relative min-h-[320px]">
              {/* Session Switcher */}
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setActive('asia')}
                  className={`flex items-center gap-2 px-6 py-2 rounded-full font-semibold transition-all duration-200 text-base focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 shadow-md border-2 ${active === 'asia' ? 'bg-gradient-to-r from-[#2D7D46] to-[#3FA65B] text-white border-[#3FA65B] scale-105' : 'bg-[#181f2b] text-gray-300 border-gray-700 hover:border-[#2D7D46]/60'}`}
                  aria-pressed={active === 'asia'}
                >
                  <span className="text-xl">🌏</span>
                  <span>Asia</span>
                </button>
                <button
                  onClick={() => setActive('us')}
                  className={`flex items-center gap-2 px-6 py-2 rounded-full font-semibold transition-all duration-200 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 shadow-md border-2 ${active === 'us' ? 'bg-gradient-to-r from-blue-700 to-blue-400 text-white border-blue-400 scale-105' : 'bg-[#181f2b] text-gray-300 border-gray-700 hover:border-blue-400/60'}`}
                  aria-pressed={active === 'us'}
                >
                  <span className="text-xl">🇺🇸</span>
                  <span>US</span>
                </button>
              </div>
              {/* Session Info */}
              <div className="flex flex-col items-center mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                    <title>Calendar Icon</title>
                    <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path d="M16 3v4M8 3v4M3 9h18" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="font-bold text-lg md:text-xl text-white tracking-wide">
                    {active === 'asia' ? 'Asia Session' : 'US Session'}
                  </span>
                </div>
                <span className="text-gray-300 text-base font-mono">
                  {SESSIONS[active].date}
                </span>
              </div>
              {/* Dramatic Timer */}
              <div className="flex justify-center gap-3 mb-2">
                <div className="flex flex-col items-center">
                  <div className="text-5xl md:text-6xl font-mono font-extrabold text-green-300 drop-shadow-glow animate-pulse">
                    {timeLeft.days.toString().padStart(2, '0')}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 uppercase tracking-widest">days</span>
                </div>
                <div className="text-5xl font-bold text-white flex items-end pb-2">:</div>
                <div className="flex flex-col items-center">
                  <div className="text-5xl md:text-6xl font-mono font-extrabold text-green-300 drop-shadow-glow animate-pulse">
                    {timeLeft.hours.toString().padStart(2, '0')}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 uppercase tracking-widest">hours</span>
                </div>
                <div className="text-5xl font-bold text-white flex items-end pb-2">:</div>
                <div className="flex flex-col items-center">
                  <div className="text-5xl md:text-6xl font-mono font-extrabold text-green-300 drop-shadow-glow animate-pulse">
                    {timeLeft.minutes.toString().padStart(2, '0')}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 uppercase tracking-widest">minutes</span>
                </div>
                <div className="text-5xl font-bold text-white flex items-end pb-2">:</div>
                <div className="flex flex-col items-center">
                  <div className="text-5xl md:text-6xl font-mono font-extrabold text-green-300 drop-shadow-glow animate-pulse">
                    {timeLeft.seconds.toString().padStart(2, '0')}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 uppercase tracking-widest">seconds</span>
                </div>
              </div>
              <span className="text-sm text-gray-400 mb-3">{active === 'asia' ? 'Indian Standard Time (IST)' : 'Eastern Standard Time (EST)'}</span>
              <div className="w-full flex flex-col items-center mt-2">
                <button
                  className="px-8 py-3 bg-gradient-to-r from-[#2D7D46] to-[#3FA65B] text-white font-bold rounded-full shadow-lg hover:scale-105 hover:shadow-xl transition-all duration-200 text-lg tracking-wide focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
                  onClick={() => setIsWebinarDialogOpen(true)}
                >
                  Register Now
                </button> 
              </div>
              <style>{`
                .glassmorphism {
                  backdrop-filter: blur(18px) saturate(140%);
                  background: linear-gradient(112deg, rgba(19,28,43,0.9) 60%, rgba(61,125,70,0.08) 100%);
                  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.12);
                  border: 1.5px solid rgba(48, 255, 163, 0.11);
                }
                .drop-shadow-glow {
                  text-shadow: 0 0 8px #3FA65B, 0 0 16px #2D7D46;
                }
              `}</style>
            </div>
          </div>
        </div>
        {/* Divider for symmetry on desktop */}
        <div className="hidden md:block w-px bg-gradient-to-b from-gray-800 via-[#2D7D46]/30 to-gray-800 mx-6 rounded-full" />
        {/* RIGHT: Panelists */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center">
  <h2 className="text-center text-3xl md:text-4xl font-bold text-white w-full">
    Meet your Host
  </h2>
  <p className="mt-2 mb-6 text-center text-gray-400 text-lg w-full">
    Human + AI. Live on stage, for the first time.
  </p>
          <div className="flex flex-col w-full items-center gap-4">
  {/* Top row: Vijay & Shoaib */}
  <div className="flex flex-row justify-center gap-3 w-full max-w-md mx-auto">
  {panelists.slice(0, 2).map((p) => (
    <motion.div
      key={p.name}
      whileHover={{ boxShadow: '0 0 0 3px #3FA65B, 0 0 16px #2D7D46' }}
      className="relative group bg-[#181f2b]/90 rounded-xl border border-gray-700 p-3 flex flex-col items-center transition-all duration-300 overflow-hidden min-h-[160px] max-w-[140px] w-full flex-1"
    >
      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#2D7D46] mb-2 relative">
        <img
          src={p.image}
          alt={p.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="text-sm font-bold text-white mb-0.5 text-center">{p.name}</div>
      <div className="text-xs text-gray-400 mb-0.5 text-center">{p.title}</div>
    </motion.div>
  ))}
</div>
  {/* Bottom row: CORAA */}
  <div className="flex flex-row justify-center w-full mt-1">
  {panelists.filter((p) => p.isCoraa).map((p) => (
    <motion.div
      key={p.name}
      whileHover={{ boxShadow: '0 0 0 3px #3FA65B, 0 0 16px #2D7D46' }}
      className="relative group bg-[#181f2b]/90 rounded-xl border border-gray-700 p-3 flex flex-col items-center transition-all duration-300 overflow-hidden min-h-[130px] max-w-[140px] w-full flex-1 shadow-[0_0_10px_1px_#3FA65B66]"
    >
      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#2D7D46] mb-2 relative">
        <img
          src={p.image}
          alt={p.name}
          className="w-full h-full object-cover animate-coraa-shimmer"
        />
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3FA65B99] to-transparent opacity-60 animate-shimmer pointer-events-none rounded-full" />
      </div>
      <div className="text-sm font-bold text-white mb-0.5 text-center">{p.name}</div>
      <div className="text-xs text-gray-400 mb-0.5 text-center">{p.title}</div>
    </motion.div>
  ))}
</div>
</div>
        </div>
      </div>
      {isWebinarDialogOpen && (
        <WebinarDialog
          isOpen={isWebinarDialogOpen}
          onClose={() => setIsWebinarDialogOpen(false)}
        />
      )}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, #3FA65B44 50%, transparent 100%);
          background-size: 200px 100%;
          animation: shimmer 1.5s infinite linear;
        }
        .animate-coraa-shimmer {
          animation: shimmer 2s infinite linear;
        }
      `}</style>
    </section>
  );
};

export default WebinarDetailsSection;
